import React from "react";
import { GPUInstanceType } from "types/InstanceTypes";
import { Checkbox } from "components/Graphs/Checkbox";
import { Input } from "components/Graphs/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/Graphs/Select";

interface LocationSubLocationPickerProps {
  instance: GPUInstanceType | undefined;
  location: string;
  onLocationChange: (location: string) => void;
  subLocation: string;
  onSubLocationChange: (subLocation: string) => void;
  allowCustomValues?: boolean;
}

const LocationSubLocationPicker: React.FC<LocationSubLocationPickerProps> = ({
  instance,
  location,
  onLocationChange,
  subLocation,
  onSubLocationChange,
  allowCustomValues = false,
}) => {
  const availableLocations = instance?.available_locations || [];
  const hasAvailableLocations = availableLocations.length > 0;

  const handleLocationChange = (newLocation: string) => {
    onLocationChange(newLocation);
    onSubLocationChange("");
  };

  const matchingSublocationValues =
    location && instance?.available_sublocations
      ? instance.available_sublocations
          .filter((sub) => sub.startsWith(`${location}:`))
          .map((sub) => sub.slice(location.length + 1))
      : [];
  const isKnownLocation = !location || availableLocations.includes(location);
  const isKnownSubLocation =
    !subLocation || matchingSublocationValues.includes(subLocation);
  const [useCustomLocation, setUseCustomLocation] = React.useState(
    () =>
      allowCustomValues &&
      hasAvailableLocations &&
      !!location &&
      !isKnownLocation,
  );
  const [useCustomSubLocation, setUseCustomSubLocation] = React.useState(
    () => allowCustomValues && !!subLocation && !isKnownSubLocation,
  );
  const previousInstanceType = React.useRef(instance?.type);

  React.useEffect(() => {
    if (previousInstanceType.current === instance?.type) {
      return;
    }

    previousInstanceType.current = instance?.type;
    setUseCustomLocation(false);
    setUseCustomSubLocation(false);
  }, [instance?.type]);

  React.useEffect(() => {
    if (
      allowCustomValues &&
      hasAvailableLocations &&
      location &&
      !isKnownLocation
    ) {
      setUseCustomLocation(true);
    }
  }, [allowCustomValues, hasAvailableLocations, isKnownLocation, location]);

  React.useEffect(() => {
    if (allowCustomValues && subLocation && !isKnownSubLocation) {
      setUseCustomSubLocation(true);
    }
  }, [allowCustomValues, isKnownSubLocation, subLocation]);

  const shouldShowCustomLocationToggle =
    allowCustomValues && hasAvailableLocations;
  const shouldShowLocationInput = useCustomLocation || !hasAvailableLocations;
  const shouldShowSubLocation =
    !!instance && (allowCustomValues || matchingSublocationValues.length > 0);
  const shouldShowCustomSubLocationToggle =
    allowCustomValues && matchingSublocationValues.length > 0;
  const shouldShowSubLocationInput =
    useCustomSubLocation ||
    (allowCustomValues && matchingSublocationValues.length === 0);

  return (
    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <div className="flex flex-row items-center mb-1 gap-3">
          <label className="block text-sm font-medium text-secondary">
            Default location (optional)
          </label>
          {shouldShowCustomLocationToggle && (
            <label className="flex items-center gap-2 text-sm text-secondary cursor-pointer">
              <Checkbox
                checked={useCustomLocation}
                onCheckedChange={(checked) => {
                  const isChecked = checked === true;
                  setUseCustomLocation(isChecked);
                  if (!isChecked) {
                    handleLocationChange("");
                  }
                }}
                disabled={!instance}
              />
              Custom
            </label>
          )}
        </div>
        {shouldShowLocationInput ? (
          <Input
            placeholder="e.g. us-west-2"
            value={location}
            onChange={(e) => handleLocationChange(e.target.value)}
            disabled={!instance}
          />
        ) : (
          <Select
            value={location || "__none__"}
            onValueChange={(val) =>
              handleLocationChange(val === "__none__" ? "" : val)
            }
            disabled={!instance}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select location" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">None</SelectItem>
              {availableLocations.map((loc) => (
                <SelectItem key={loc} value={loc}>
                  {loc}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      {shouldShowSubLocation && (
        <div>
          <div className="flex flex-row items-center mb-1 gap-3">
            <label className="block text-sm font-medium text-secondary">
              Default sub-location (optional)
            </label>
            {shouldShowCustomSubLocationToggle && (
              <label className="flex items-center gap-2 text-sm text-secondary cursor-pointer">
                <Checkbox
                  checked={useCustomSubLocation}
                  onCheckedChange={(checked) => {
                    const isChecked = checked === true;
                    setUseCustomSubLocation(isChecked);
                    if (!isChecked) {
                      onSubLocationChange("");
                    }
                  }}
                  disabled={!instance}
                />
                Custom
              </label>
            )}
          </div>
          {shouldShowSubLocationInput ? (
            <Input
              placeholder="e.g. us-west-2a"
              value={subLocation}
              onChange={(e) => onSubLocationChange(e.target.value)}
              disabled={!instance}
            />
          ) : (
            <Select
              value={subLocation || "__none__"}
              onValueChange={(val) =>
                onSubLocationChange(val === "__none__" ? "" : val)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select sub-location" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None</SelectItem>
                {matchingSublocationValues.map((sub) => (
                  <SelectItem key={sub} value={sub}>
                    {sub}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      )}
    </div>
  );
};

export default LocationSubLocationPicker;
